import { Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import gsap from 'gsap';
import { Scene } from '../engine/scene';
import { getContext } from '../engine/context';
import { Button } from '../ui/Button';
import { CoinBalance } from '../ui/CoinBalance';
import { GAME_HEIGHT, GAME_WIDTH } from '../config/tuning';
import { between, betweenInt } from '../engine/math';

/**
 * MenuScene — "Neon Arcade" layout.
 *
 * Vertical composition (top → bottom):
 *   1. Top-right: coin balance inside a small dark panel.
 *   2. Title group: SPLIFFO wordmark + JOINT SIMULATOR neon tag.
 *   3. Primary CTA: START (largest, glowing).
 *   4. Secondary row: HOW TO PLAY · HIGH SCORES (matched, same color family).
 *   5. Premium CTA: SHOP (below, with a soft gold halo + sparkle ring).
 *   6. Footer hint line.
 */
export class MenuScene extends Scene {
  async onEnter(): Promise<void> {
    const ctx = getContext();
    this.sortableChildren = true;

    const cx = GAME_WIDTH / 2;

    this.buildAmbientClouds();
    this.buildCoinPanel();
    this.buildTitleGroup(cx);
    this.buildStartButton(cx, ctx);
    this.buildSecondaryButtons(cx, ctx);
    this.buildShopButton(cx, ctx);
    this.buildFooter(cx);
  }

  // ── Layers ────────────────────────────────────────────────────────────────

  private buildAmbientClouds(): void {
    for (let i = 0; i < 3; i++) {
      const cloud = new Sprite(
        Texture.from(i % 2 === 0 ? 'smoke_cloud_drift_1' : 'smoke_cloud_drift_2'),
      );
      cloud.anchor.set(0.5);
      cloud.position.set(
        betweenInt(GAME_WIDTH * 0.1, GAME_WIDTH * 0.9),
        betweenInt(GAME_HEIGHT * 0.08, GAME_HEIGHT * 0.3),
      );
      cloud.alpha = 0.16;
      cloud.scale.set(0.85);
      cloud.zIndex = 1;
      this.addChild(cloud);
      gsap.to(cloud, {
        x: cloud.x + between(-140, 140),
        y: cloud.y - 16,
        duration: between(10, 15),
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
      });
    }
  }

  private buildCoinPanel(): void {
    // A small panel in the top-right that houses the coin balance, so the
    // number doesn't float in empty space. Non-interactive.
    const panel = new Container();
    panel.position.set(GAME_WIDTH - 260, 50);
    panel.zIndex = 50;

    const bg = new Graphics()
      .roundRect(-110, -32, 220, 64, 32)
      .fill({ color: 0x0f0a1a, alpha: 0.85 })
      .stroke({ color: 0x5fdbcf, width: 2.5, alpha: 0.9 });
    panel.addChild(bg);

    const coins = new CoinBalance({
      x: -40,
      y: 0,
      scale: 0.9,
      fontSize: 28,
    });
    panel.addChild(coins);

    this.addChild(panel);
  }

  private buildTitleGroup(cx: number): void {
    const group = new Container();
    group.position.set(cx, GAME_HEIGHT * 0.22);
    group.zIndex = 10;
    this.addChild(group);

    // Soft neon halo behind the logo — an elongated glowing ellipse.
    const halo = new Graphics()
      .ellipse(0, 20, 480, 150)
      .fill({ color: 0xff6bb0, alpha: 0.12 });
    halo.zIndex = 0;
    group.addChild(halo);
    gsap.to(halo, { alpha: 0.22, duration: 2.8, yoyo: true, repeat: -1, ease: 'sine.inOut' });

    // Logo wordmark (the SPLIFFO SVG).
    const logo = new Sprite(Texture.from('ui_logo'));
    logo.anchor.set(0.5);
    logo.position.set(0, 0);
    logo.scale.set(1.0);
    logo.zIndex = 2;
    group.addChild(logo);
    gsap.to(logo, { y: -10, duration: 2.6, yoyo: true, repeat: -1, ease: 'sine.inOut' });

    // Subtitle tag — a thin pill frame with neon text.
    const subWrap = new Container();
    subWrap.position.set(0, 140);
    subWrap.zIndex = 3;
    group.addChild(subWrap);

    const subText = new Text({
      text: 'JOINT SIMULATOR',
      style: {
        fontFamily: 'Impact, Arial Black, sans-serif',
        fontSize: 28,
        fill: 0x5fdbcf,
        letterSpacing: 12,
        stroke: { color: 0x0a0510, width: 3 },
      },
    });
    subText.anchor.set(0.5);

    const padX = 26;
    const padY = 10;
    const tagBg = new Graphics()
      .roundRect(
        -subText.width / 2 - padX,
        -subText.height / 2 - padY,
        subText.width + padX * 2,
        subText.height + padY * 2,
        (subText.height + padY * 2) / 2,
      )
      .fill({ color: 0x0f0a1a, alpha: 0.75 })
      .stroke({ color: 0x5fdbcf, width: 2, alpha: 0.8 });
    subWrap.addChild(tagBg);
    subWrap.addChild(subText);

    gsap.to(subText, { alpha: 0.65, duration: 2.4, yoyo: true, repeat: -1, ease: 'sine.inOut' });
  }

  private buildStartButton(cx: number, ctx: ReturnType<typeof getContext>): void {
    const y = GAME_HEIGHT * 0.57;

    // Pulsing neon halo behind the primary CTA.
    const halo = new Graphics()
      .ellipse(0, 0, 240, 80)
      .fill({ color: 0x5fdbcf, alpha: 0.18 });
    halo.position.set(cx, y);
    halo.zIndex = 10;
    this.addChild(halo);
    gsap.to(halo, { alpha: 0.32, duration: 1.6, yoyo: true, repeat: -1, ease: 'sine.inOut' });

    const startBtn = new Button({
      x: cx,
      y,
      idleKey: 'ui_btn_start',
      hoverKey: 'ui_btn_start_hover',
      onClick: () => ctx.sceneManager.start('DifficultyScene'),
      audio: ctx.audio,
    });
    startBtn.scale.set(1.2);
    startBtn.zIndex = 11;
    this.addChild(startBtn);
  }

  private buildSecondaryButtons(cx: number, ctx: ReturnType<typeof getContext>): void {
    const y = GAME_HEIGHT * 0.74;
    const offset = 300;
    const scale = 0.8;

    const htBtn = new Button({
      x: cx - offset,
      y,
      idleKey: 'ui_btn_howto',
      hoverKey: 'ui_btn_howto_hover',
      onClick: () => ctx.sceneManager.start('TutorialScene'),
      audio: ctx.audio,
    });
    htBtn.scale.set(scale);
    htBtn.zIndex = 11;
    this.addChild(htBtn);

    const hsBtn = new Button({
      x: cx + offset,
      y,
      idleKey: 'ui_btn_hs',
      hoverKey: 'ui_btn_hs_hover',
      onClick: () => ctx.sceneManager.start('HighScoreScene', { from: 'MenuScene' }),
      audio: ctx.audio,
    });
    hsBtn.scale.set(scale);
    hsBtn.zIndex = 11;
    this.addChild(hsBtn);
  }

  private buildShopButton(cx: number, ctx: ReturnType<typeof getContext>): void {
    const y = GAME_HEIGHT * 0.88;

    // Premium halo behind the shop button (gold-leaning).
    const halo = new Graphics()
      .ellipse(0, 0, 220, 60)
      .fill({ color: 0xffd23f, alpha: 0.2 });
    halo.position.set(cx, y);
    halo.zIndex = 10;
    this.addChild(halo);
    gsap.to(halo, { alpha: 0.38, duration: 1.4, yoyo: true, repeat: -1, ease: 'sine.inOut' });

    const shopBtn = new Button({
      x: cx,
      y,
      idleKey: 'ui_btn_shop',
      hoverKey: 'ui_btn_shop_hover',
      onClick: () => ctx.sceneManager.start('ShopScene'),
      audio: ctx.audio,
    });
    shopBtn.scale.set(0.75);
    shopBtn.zIndex = 11;
    this.addChild(shopBtn);

    // Sparkle ring around the shop — 6 small animated sparkles.
    const sparkles: Sprite[] = [];
    const count = 6;
    const rx = 200;
    const ry = 46;
    for (let i = 0; i < count; i++) {
      const sp = new Sprite(Texture.from('particle_sparkle'));
      sp.anchor.set(0.5);
      const angle = (i / count) * Math.PI * 2;
      sp.position.set(cx + Math.cos(angle) * rx, y + Math.sin(angle) * ry);
      sp.scale.set(0.8);
      sp.alpha = 0.75;
      sp.tint = 0xffd23f;
      sp.zIndex = 12;
      this.addChild(sp);
      sparkles.push(sp);
      gsap.to(sp, {
        alpha: 0.3,
        duration: 0.9 + i * 0.12,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
        delay: i * 0.08,
      });
      gsap.to(sp.scale, {
        x: 1.1,
        y: 1.1,
        duration: 0.9 + i * 0.12,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
        delay: i * 0.08,
      });
    }
  }

  private buildFooter(cx: number): void {
    const hint = new Text({
      text: 'Hold SPACE to inhale  ·  Beat the bot for the longest streak',
      style: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 20,
        fill: 0x88889a,
        align: 'center',
        letterSpacing: 1,
      },
    });
    hint.anchor.set(0.5);
    hint.position.set(cx, GAME_HEIGHT - 30);
    hint.zIndex = 20;
    this.addChild(hint);
  }
}
